import { useState, useEffect } from 'react';
import { Sun, Sunrise, Moon, Flame, Calendar, Compass } from 'lucide-react';
import { supabase } from '../services/supabase';

interface TodayHeroProps {
  userName: string;
  streak: number;
  journalCompleted: boolean;
  planLocked: boolean;
  coachEngaged: boolean;
  actionTaken: boolean;
}

interface WeeklyFocus {
  topValue: string;
  daysLeft: number;
}

const DAILY_MESSAGES = [
  "Small steps lead to big changes.",
  "Today is another chance to grow.",
  "Progress, not perfection.",
  "You're building something great.",
  "One day at a time.",
  "Show up for yourself today.",
  "Your consistency is your superpower.",
  "Trust the process.",
  "Every expert was once a beginner.",
  "Make today count."
];

function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function getGreeting(timeOfDay: 'morning' | 'afternoon' | 'evening'): string {
  switch (timeOfDay) {
    case 'morning': return 'Good morning';
    case 'afternoon': return 'Good afternoon';
    case 'evening': return 'Good evening';
  }
}

function getTimeIcon(timeOfDay: 'morning' | 'afternoon' | 'evening') {
  switch (timeOfDay) {
    case 'morning': return Sunrise;
    case 'afternoon': return Sun;
    case 'evening': return Moon;
  }
}

function getDailyMessage(): string {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  return DAILY_MESSAGES[dayOfYear % DAILY_MESSAGES.length];
}

export function TodayHero({
  userName,
  streak,
  journalCompleted,
  planLocked,
  coachEngaged,
  actionTaken
}: TodayHeroProps) {
  const [mounted, setMounted] = useState(false);
  const [weeklyFocus, setWeeklyFocus] = useState<WeeklyFocus | null>(null);
  const timeOfDay = getTimeOfDay();
  const TimeIcon = getTimeIcon(timeOfDay);

  useEffect(() => {
    setMounted(true);
    loadWeeklyFocus();
  }, []);

  async function loadWeeklyFocus() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('weekly_plan_values, weekly_plan_locked_at')
        .eq('id', user.id)
        .maybeSingle();

      if (profile?.weekly_plan_locked_at && profile.weekly_plan_values?.length > 0) {
        const lockedDate = new Date(profile.weekly_plan_locked_at);
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());

        if (lockedDate >= weekStart) {
          const today = new Date();
          const dayOfWeek = today.getDay();
          const daysLeft = dayOfWeek === 0 ? 7 : 7 - dayOfWeek;

          setWeeklyFocus({
            topValue: profile.weekly_plan_values[0],
            daysLeft
          });
        }
      }
    } catch (error) {
      console.error('Error loading weekly focus:', error);
    }
  }

  const tasks = [
    { key: 'journal', label: 'Journal', completed: journalCompleted },
    { key: 'plan', label: 'Plan', completed: planLocked },
    { key: 'coach', label: 'Coach', completed: coachEngaged },
    { key: 'action', label: 'Action', completed: actionTaken }
  ];

  const completedCount = tasks.filter(t => t.completed).length;
  const progress = (completedCount / tasks.length) * 100;
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const gradientClasses = {
    morning: 'from-amber-50 via-orange-50 to-rose-50',
    afternoon: 'from-sky-50 via-blue-50 to-cyan-50',
    evening: 'from-slate-100 via-zinc-100 to-stone-100'
  };

  const accentColors = {
    morning: 'text-amber-600',
    afternoon: 'text-sky-600',
    evening: 'text-slate-600'
  };

  return (
    <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${gradientClasses[timeOfDay]} p-6 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/30 rounded-full -mr-32 -mt-32 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/20 rounded-full -ml-24 -mb-24 blur-2xl" />

      <div className="relative flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <TimeIcon className={`h-5 w-5 ${accentColors[timeOfDay]}`} />
            <span className="text-sm text-gray-500 flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5" />
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {getGreeting(timeOfDay)}{userName ? `, ${userName}` : ''}
          </h1>

          <p className="text-gray-600 text-sm md:text-base max-w-xs">
            {getDailyMessage()}
          </p>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            {weeklyFocus && (
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 shadow-sm transition-all duration-500 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: '150ms' }}>
                <Compass className="h-3.5 w-3.5 text-amber-600" />
                <span className="text-sm font-semibold text-amber-900">{weeklyFocus.topValue}</span>
                <span className="text-xs text-amber-600">• {weeklyFocus.daysLeft}d left</span>
              </div>
            )}
            {streak > 0 && (
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/60 backdrop-blur-sm border border-white/80 shadow-sm transition-all duration-500 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: '200ms' }}>
                <Flame className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-semibold text-gray-800">{streak} day streak</span>
              </div>
            )}
          </div>
        </div>

        <div className={`relative w-24 h-24 flex-shrink-0 transition-all duration-700 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`} style={{ transitionDelay: '100ms' }}>
          <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="white"
              strokeWidth="8"
              className="opacity-60"
            />
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="url(#heroProgressGradient)"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-1000 ease-out"
            />
            <defs>
              <linearGradient id="heroProgressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#ea580c" />
              </linearGradient>
            </defs>
          </svg>

          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-gray-900">{completedCount}</span>
            <span className="text-xs text-gray-500">of {tasks.length}</span>
          </div>
        </div>
      </div>

      <div className={`flex gap-2 mt-5 transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`} style={{ transitionDelay: '300ms' }}>
        {tasks.map((task, index) => (
          <div
            key={task.key}
            className={`flex-1 py-2 px-3 rounded-xl text-center text-xs font-medium transition-all duration-300 ${
              task.completed
                ? 'bg-white/80 text-gray-900 shadow-sm border border-white'
                : 'bg-white/40 text-gray-500'
            }`}
            style={{ transitionDelay: `${400 + index * 50}ms` }}
          >
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${task.completed ? 'bg-amber-500' : 'bg-gray-300'}`} />
            {task.label}
          </div>
        ))}
      </div>
    </div>
  );
}
